import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, DatePicker, Descriptions, Input, InputNumber, Modal, Radio, Select, Space, Table, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import api from '../../../services/api';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const HISTORICAL_SAFE_METRICS = ['sales', 'category'];
const METRIC_TIME_ZONE = 'America/Los_Angeles';
const SNAPSHOT_SOURCE_OPTIONS = [
  { value: 'manual_recompute', label: 'Manual recompute' },
  { value: 'manual_daily_trigger', label: 'Manual daily trigger' },
  { value: 'rolling_correction', label: 'Rolling correction' }
];

const getDateInMetricTimezone = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: METRIC_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};

const addDays = (dateString, days) => {
  const [year, month, day] = String(dateString).split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const getDefaultMetricDate = () => addDays(getDateInMetricTimezone(), -1);

const MetricsBackfillModal = ({ open, onCancel }) => {
  const { sellerAccounts } = useSelector(state => state.dataCommon);
  const [mode, setMode] = useState('range');
  const [dateRange, setDateRange] = useState(() => {
    const metricDate = getDefaultMetricDate();
    return [dayjs(addDays(metricDate, -6)), dayjs(metricDate)];
  });
  const [metricDate, setMetricDate] = useState(() => dayjs(getDefaultMetricDate()));
  const [rollingDays, setRollingDays] = useState(7);
  const [sellerIds, setSellerIds] = useState([]);
  const [skuText, setSkuText] = useState('');
  const [batchSize, setBatchSize] = useState(2000);
  const [snapshotSource, setSnapshotSource] = useState('manual_recompute');
  const [metrics, setMetrics] = useState(HISTORICAL_SAFE_METRICS);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [periodCacheQueueing, setPeriodCacheQueueing] = useState(false);

  const selectedRange = useMemo(() => {
    if (mode === 'single' && metricDate) {
      const date = metricDate.format('YYYY-MM-DD');
      return { dateFrom: date, dateTo: date };
    }
    if (mode === 'rolling') {
      const safeRollingDays = Math.min(Math.max(parseInt(rollingDays, 10) || 7, 1), 30);
      const dateTo = addDays(getDateInMetricTimezone(), -1);
      return {
        dateFrom: addDays(dateTo, -safeRollingDays + 1),
        dateTo
      };
    }
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      return {
        dateFrom: dateRange[0].format('YYYY-MM-DD'),
        dateTo: dateRange[1].format('YYYY-MM-DD')
      };
    }
    return { dateFrom: null, dateTo: null };
  }, [dateRange, metricDate, mode, rollingDays]);

  const defaultMetricDate = getDefaultMetricDate();
  const isHistoricalSkuLocked = mode === 'rolling' || (selectedRange.dateFrom && selectedRange.dateFrom < defaultMetricDate);
  const isFutureDateSelected = selectedRange.dateTo && selectedRange.dateTo > defaultMetricDate;
  const effectiveMetrics = isHistoricalSkuLocked
    ? metrics.filter(metric => HISTORICAL_SAFE_METRICS.includes(metric))
    : metrics;

  useEffect(() => {
    if (!isHistoricalSkuLocked) return;
    setMetrics(prev => {
      const safeMetrics = prev.filter(metric => HISTORICAL_SAFE_METRICS.includes(metric));
      return safeMetrics.length > 0 ? safeMetrics : HISTORICAL_SAFE_METRICS;
    });
    if (mode === 'rolling') setSnapshotSource('rolling_correction');
    else setSnapshotSource('manual_recompute');
  }, [isHistoricalSkuLocked, mode]);

  useEffect(() => {
    if (!open) return;
    setBatchSize(2000);
    setPreview(null);
  }, [open]);

  const disableAfterDefaultMetricDate = (current) => {
    if (!current) return false;
    return current.format('YYYY-MM-DD') > defaultMetricDate;
  };

  const buildPayload = () => {
    let dateFrom;
    let dateTo;
    let finalSnapshotSource = snapshotSource;

    if (mode === 'single') {
      if (!metricDate) throw new Error('Metric date is required');
      dateFrom = metricDate.format('YYYY-MM-DD');
      dateTo = dateFrom;
      finalSnapshotSource = snapshotSource || 'manual_daily_trigger';
    } else if (mode === 'rolling') {
      const safeRollingDays = Math.min(Math.max(parseInt(rollingDays, 10) || 7, 1), 30);
      dateTo = addDays(getDateInMetricTimezone(), -1);
      dateFrom = addDays(dateTo, -safeRollingDays + 1);
      finalSnapshotSource = 'rolling_correction';
    } else {
      if (!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
        throw new Error('Date range is required');
      }
      dateFrom = dateRange[0].format('YYYY-MM-DD');
      dateTo = dateRange[1].format('YYYY-MM-DD');
    }

    if (isFutureDateSelected) {
      throw new Error(`Metric date cannot be after ${defaultMetricDate} (${METRIC_TIME_ZONE})`);
    }

    if (!effectiveMetrics.length) {
      throw new Error('Select at least one metric');
    }

    const skuTokens = skuText
      .split(/[\n,]+/)
      .map(item => item.trim())
      .filter(Boolean);
    const skuIds = skuTokens.filter(item => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(item));
    const skus = skuTokens.filter(item => !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(item));

    return {
      dateFrom,
      dateTo,
      metrics: effectiveMetrics,
      sellerIds,
      skuIds,
      skus,
      batchSize,
      snapshotSource: finalSnapshotSource,
      mode: 'recompute_from_db'
    };
  };

  const handlePreview = async () => {
    setLoadingPreview(true);
    try {
      const payload = buildPayload();
      const response = await api.post('/inventory/metrics/backfill/preview', payload);
      setPreview(response.data);
    } catch (error) {
      message.error(error.response?.data?.message || error.message || 'Failed to preview recompute');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleRun = async () => {
    setQueueing(true);
    try {
      const payload = buildPayload();
      const response = await api.post('/inventory/metrics/backfill', {
        ...payload
      });
      message.success(response.data?.message || 'Metrics recompute job queued');
      onCancel();
      setPreview(null);
    } catch (error) {
      message.error(error.response?.data?.message || error.message || 'Failed to queue recompute');
    } finally {
      setQueueing(false);
    }
  };

  const handleRefreshPeriodCache = async () => {
    setPeriodCacheQueueing(true);
    try {
      const response = await api.post('/inventory/metrics/period-cache/refresh');
      message.success(response.data?.message || 'Sales period cache refresh job queued');
    } catch (error) {
      message.error(error.response?.data?.message || error.message || 'Failed to queue sales period cache refresh');
    } finally {
      setPeriodCacheQueueing(false);
    }
  };

  const sampleColumns = [
    { title: 'SKU', dataIndex: 'sku', key: 'sku', width: 180 },
    { title: 'Metric Date', dataIndex: 'metricDate', key: 'metricDate', width: 120 },
    {
      title: 'Current Snapshot',
      key: 'currentSnapshot',
      render: (_, record) => {
        const snapshot = record.currentSnapshot || {};
        return (
          <Space size={8} wrap>
            <Text>Sale: {snapshot.salesCount ?? '-'}</Text>
            <Text>Category: {snapshot.categoryId ? String(snapshot.categoryId).slice(0, 8) : '-'}</Text>
            <Text>Rank: {snapshot.mainRank ? `#${Number(snapshot.mainRank).toLocaleString()}` : '-'}</Text>
            <Text>QC: {snapshot.normalizedRank ? `#${Number(snapshot.normalizedRank).toLocaleString()}` : '-'}</Text>
            {record.salesCount && <Text>New Sale: {record.salesCount.new ?? '-'}</Text>}
          </Space>
        );
      }
    }
  ];

  return (
    <Modal
      title="Trigger collect-sku-daily-metrics"
      open={open}
      onCancel={onCancel}
      width={760}
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button key="period-cache" onClick={handleRefreshPeriodCache} loading={periodCacheQueueing}>
          Refresh Period Cache
        </Button>,
        <Button key="preview" onClick={handlePreview} loading={loadingPreview} disabled={!effectiveMetrics.length || isFutureDateSelected}>Preview</Button>,
        <Button key="run" type="primary" onClick={handleRun} loading={queueing} disabled={!preview || !effectiveMetrics.length || isFutureDateSelected}>
          Queue Recompute
        </Button>
      ]}
      destroyOnClose={false}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={14}>
        <Alert
          type="info"
          showIcon
          message={`Trigger thủ công job collect-sku-daily-metrics theo ngày metric ${METRIC_TIME_ZONE}. Preview trước để biết scope và số dòng sẽ insert/update.`}
        />
        <Alert
          type="warning"
          showIcon
          message="Refresh Period Cache chỉ queue job refresh-listing-sales để cập nhật sales_7d/30d/90d từ sku_daily_metrics hiện có. Hãy chạy sau khi metrics recompute đã hoàn tất nếu vừa backfill sales."
        />

        <div>
          <Text strong>Trigger Mode</Text>
          <div style={{ marginTop: 6 }}>
            <Radio.Group
              value={mode}
              onChange={(event) => {
                const nextMode = event.target.value;
                setMode(nextMode);
                if (nextMode === 'rolling') setSnapshotSource('rolling_correction');
                if (nextMode === 'single') setSnapshotSource('manual_daily_trigger');
                if (nextMode === 'range') setSnapshotSource('manual_recompute');
                setPreview(null);
              }}
            >
              <Space direction="vertical">
                <Radio value="single">Single metric date</Radio>
                <Radio value="range">Date range / backfill</Radio>
                <Radio value="rolling">Rolling correction</Radio>
              </Space>
            </Radio.Group>
          </div>
        </div>

        {mode === 'single' && (
          <div>
            <Text strong>Metric Date</Text>
            <div style={{ marginTop: 6 }}>
              <DatePicker
                value={metricDate}
                onChange={(value) => {
                  setMetricDate(value);
                  setPreview(null);
                }}
                allowClear={false}
                format="YYYY-MM-DD"
                disabledDate={disableAfterDefaultMetricDate}
              />
            </div>
          </div>
        )}

        {mode === 'range' && (
          <div>
          <Text strong>Date Range</Text>
          <div style={{ marginTop: 6 }}>
            <RangePicker
              value={dateRange}
              onChange={(value) => {
                setDateRange(value);
                setPreview(null);
              }}
              allowClear={false}
              format="YYYY-MM-DD"
              disabledDate={disableAfterDefaultMetricDate}
            />
          </div>
        </div>
        )}

        {mode === 'rolling' && (
          <div>
            <Text strong>Rolling Days</Text>
            <div style={{ marginTop: 6 }}>
              <InputNumber
                min={1}
                max={30}
                value={rollingDays}
                onChange={(value) => {
                  setRollingDays(value || 1);
                  setPreview(null);
                }}
              />
              <Text type="secondary" style={{ marginLeft: 8 }}>
                Recompute từ hôm qua lùi về N ngày.
              </Text>
            </div>
          </div>
        )}

        <div>
          <Text strong>Scope</Text>
          <Space direction="vertical" style={{ width: '100%', marginTop: 6 }} size={8}>
            <Select
              mode="multiple"
              allowClear
              placeholder="Seller accounts (blank = all sellers)"
              value={sellerIds}
              onChange={(value) => {
                setSellerIds(value);
                setPreview(null);
              }}
              options={(sellerAccounts || []).map(account => ({
                value: account.id,
                label: `${account.name || account.email || account.sellerId} (${account.sellerId})`
              }))}
              style={{ width: '100%' }}
            />
            <Input.TextArea
              value={skuText}
              onChange={(event) => {
                setSkuText(event.target.value);
                setPreview(null);
              }}
              rows={3}
              placeholder="Optional: SKU hoặc SKU item id, phân tách bằng dòng mới hoặc dấu phẩy"
            />
          </Space>
        </div>

        <div>
          <Text strong>Metrics</Text>
          {isHistoricalSkuLocked && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 6 }}
              message={`Range có ngày cũ hơn ${defaultMetricDate}. Chỉ cho recompute Sale/Category; Price/Rank/Rating/Review bị khóa để không lấy dữ liệu SKU hiện tại đè snapshot cũ.`}
            />
          )}
          <div style={{ marginTop: 6 }}>
            <Checkbox.Group
              value={metrics}
              onChange={(value) => {
                setMetrics(value);
                setPreview(null);
              }}
              options={[
                { label: 'Sale', value: 'sales' },
                { label: 'Category', value: 'category' },
                { label: 'Price', value: 'price', disabled: isHistoricalSkuLocked },
                { label: 'Rank', value: 'rank', disabled: isHistoricalSkuLocked },
                { label: 'Rank QC', value: 'normalized_rank', disabled: isHistoricalSkuLocked },
                { label: 'Rating', value: 'rating', disabled: isHistoricalSkuLocked },
                { label: 'Review', value: 'review_count', disabled: isHistoricalSkuLocked }
              ]}
            />
          </div>
        </div>

        <div>
          <Text strong>Job Options</Text>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 12,
              marginTop: 6
            }}
          >
            <div style={{ display: 'grid', gap: 6 }}>
              <Text type="secondary">Snapshot source</Text>
              <Select
                value={snapshotSource}
                onChange={(value) => {
                  setSnapshotSource(value);
                  setPreview(null);
                }}
                options={SNAPSHOT_SOURCE_OPTIONS}
                style={{ width: '100%' }}
                disabled={mode === 'rolling' || isHistoricalSkuLocked}
              />
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              <Text type="secondary">Batch size</Text>
              <InputNumber
                min={100}
                max={50000}
                step={100}
                value={batchSize}
                onChange={(value) => {
                  setBatchSize(value || 2000);
                  setPreview(null);
                }}
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>

        {preview && (
          <>
            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="Date From">{preview.dateFrom}</Descriptions.Item>
              <Descriptions.Item label="Date To">{preview.dateTo}</Descriptions.Item>
              <Descriptions.Item label="Days">{preview.days}</Descriptions.Item>
              <Descriptions.Item label="SKU Count">{Number(preview.skuCount || 0).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Sellers Processed">{Number(preview.sellerCount || 0).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Month Windows">{Number(preview.monthCount || 0).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Total Chunks">{Number(preview.totalChunks || 0).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Execution">
                {preview.executionMode === 'sequential_seller_month' ? 'Sequential' : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Total Rows">{Number(preview.totalMetricRows || 0).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Will Insert">{Number(preview.willInsert || 0).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Will Update">{Number(preview.willUpdate || 0).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Mode">{preview.mode}</Descriptions.Item>
              <Descriptions.Item label="Batch Size">{Number(preview.batchSize || batchSize).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Seller Filter">{preview.scope?.sellerIds?.length ? preview.scope.sellerIds.length : 'All'}</Descriptions.Item>
              <Descriptions.Item label="SKU Scope">{preview.scope?.skuIds?.length ? preview.scope.skuIds.length : 'All'}</Descriptions.Item>
              {preview.recomputeLogId && <Descriptions.Item label="Job">{preview.recomputeLogId}</Descriptions.Item>}
            </Descriptions>

            <Table
              size="small"
              rowKey={(record) => `${record.sku}-${record.metricDate}`}
              columns={sampleColumns}
              dataSource={preview.sampleDiffs || []}
              pagination={false}
              locale={{ emptyText: 'No existing snapshots in this range. Queue recompute to insert new rows.' }}
            />
          </>
        )}
      </Space>
    </Modal>
  );
};

export default MetricsBackfillModal;
